import React, { useEffect, useState } from 'react';
//components
import SimpleDropdown from '../../../ui/dropdowns/SimpleDropdown';
import { PoiDialogInput } from './poiDialogComponents/PoiDialogInput';
import { PoiDialogButton } from './poiDialogComponents/PoiDialogButton';
import { CustomIntersectModal } from './poiDialogComponents/CustomIntersectModal';
import { PoiGroupTooltip } from '@components/ui/tooltips/PoiTooltips';
//hooks
import useConfirmPoiSave from '../../../../hooks/pois/useConfirmPoiSave'
import usePoiGroupService from '../../../../hooks/pois/usePoiGroupsService'
//utils
import { canUserDeletePOI } from '../../../../utils/poi/canUserDeletePOI';
import { isPoiEditDisabled, isUserAdmin } from '../../../../utils/poi/isPoiEditDisabled';
import { convertToLocalTime } from '@utils/dateAndTimeUtils';

/** HELPER FUNCTIONS - help derive state when there are complex conditions **/
const getOriginalName = (selectedGroup, AltPOIName, POIName, currentPage) => {
  if(
    (selectedGroup && !selectedGroup.label.includes("All POIs")) &&
    (AltPOIName || 
    (currentPage.includes("site-map") && !AltPOIName))
  ){
    return POIName
  }

  return null
}

//this function is needed for adding a newly created POI to the POI Group selected in dropdown
//NEW POI CASE:
  //check that the user is in (and an editor of) group selected in dropdown
  //if so, add this to the poiGroups for this POI
//EXISTING POI CASE:
  //the currentGroups are the poiGroups for this POI that the user is in - no need for extra filtering, just return them
const getUsersPoiGroups = (poiId, currentGroups, usersGroups, selectedGroup) => {

  if(poiId){
    return currentGroups
  }

  if(!selectedGroup || selectedGroup.label?.includes("All POIs") || !usersGroups){
    return []
  }

  const isInUsersGroup = usersGroups.find(group => group.POIGroupID === selectedGroup.value)

  if(isInUsersGroup && isInUsersGroup.IsEditor){
    return [isInUsersGroup]
  } else {
    return []
  }
}


/**
 * Dialog box that displays data for a single POI clicked on a map
 * 
 * @param {Object} data - data for one poi, same as iwData in usePoiMap
 * @param {number} poiId 
 * @param {Function} setPoiId - when switching to group dialog, we save poi and need to update the poiid so the new poi can be saved to groups
 * @param {Object} poiDialogHandlers - from usePoiDialogHandlers
 * @param {number} selectedPoiJob - for saving pois
 * @param {number} selectedPoiGroup - to determine which fields can be edited, and for saving new POIs to selected group
 * 
 * @returns {JSX.Element}
 * 
 * @category Components - Map
 * 
 * @todo this logic is getting complex - some new components have been made to make code more readable - can it be better?
 */
const PoiDialog = ({
  data,
  poiId,
  setPoiId,
  poiDialogHandlers,
  selectedPoiJob,
  selectedPoiGroup,
  userContext
}) => {
  /** STATE TO DETERMINE IF INPUTS DISABLED **/

  // Seems like this dialog box (which is in a google map info window) renders outside the UserContext provider, causing useUser()
  // to be undefined if called in this file. Passing down useUser() from usePoiMap -> MapDialog -> PoiDialog
  const { user, userPermissions, userGroups } = userContext || {};

  if (!userContext){
    console.log("POI Dialog: No user context")
  }

  //if user on poi-edit page, should be able to edit data
  //if on site map or site map w/pig pages, it should only be viewable, not editable
  const currentPage = window.location.pathname
  const latLngEditDisabled = data.linkedPOIs?.length > 0
  const userAdmin = isUserAdmin(userGroups)

  //** POI STATE **/
  const [name, setName] = useState(data.AltPOI_Name && !selectedPoiGroup?.label.includes("All POIs") ? data.AltPOI_Name : data.POI_Name); //show alt name if there is one, otherwise show origina name
  const [originalName] = useState(getOriginalName(selectedPoiGroup, data.AltPOI_Name, data.POI_Name, currentPage)); //show original name field only if there is an alt name (fetched pois by group)
  const [showOriginalPOIName, setshowOriginalPOIName] = useState(false)

  const [description, setDescription] = useState(data.AltDescription && !selectedPoiGroup?.label.includes("All POIs") ? data.AltDescription : data.Description); //show alt description if there is one, otherwise show original description
  const [showDescription, setShowDescription] = useState(false)

  const [createdBy] = useState(data.CreatedBy)
  const [poiGroups, setPoiGroups] = useState()

  const [editDisabled, setEditDisabled] = useState(false)

  const [canDeletePOI] = useState(createdBy ? canUserDeletePOI(createdBy.UserID, { userPermissions, userGroups, user }) : null)
  
  const [distance, setDistance] = useState(data.poiMetersToPipeline);
  const [usSite, setUsSite] = useState(data.upstreamAndDownstreamAgms.upstreamAgm.Site_Name);
  const [dsSite, setDsSite] = useState(data.upstreamAndDownstreamAgms.downstreamAgm.Site_Name);
  const [distToUS, setDistToUs] = useState(data.usDsDistances.distToUS);
  const [distToDS, setDistToDs] = useState(data.usDsDistances.distToDS);
  const [snapToSelection, setSnapToSelection] = useState('Select...');

  const [estimatedPassTime, setEstimatedPasstime] = useState("")

  const [showCustomIntersectModal, setShowCustomIntersectModal] = useState(false);
  const [customIntersectError, setCustomIntersectError] = useState('');

  /** HOOKS **/
  const [ConfirmDialog, requestConfirm] = useConfirmPoiSave()
  const {fetchPOIGroupsByUser, usersGroupData} = usePoiGroupService()


  /** USE EFFECTS **/

  const getETA = async() => {
    const response = await poiDialogHandlers.getSinglePOIEstimatedPassTime(poiId)

    if(response.success){
      const {estimatedPassTime, timezone} = response.data

      const poiETA = convertToLocalTime(estimatedPassTime, timezone.value)

      if(poiETA) {
        setEstimatedPasstime(`${poiETA} (${timezone.abbr})`)

      } else {
        setEstimatedPasstime(null)
      }
    }
  }

  useEffect(() => {
    fetchPOIGroupsByUser(selectedPoiJob)

    //get estimated POI pass time, if there is a selected run
    getETA()


    //if there is a group selected, determine if user is an editor
    if(!selectedPoiGroup) return

    const userInSelectedGroup = data.poiGroups.find(group => group.POIGroupID === selectedPoiGroup.value)
    const isEditor = userInSelectedGroup && userInSelectedGroup.IsEditor

    const isEditDisabled = (!data.CreatedBy || !poiId) ? false : isPoiEditDisabled(currentPage, selectedPoiGroup, data.CreatedBy, { userGroups, user }, isEditor)//if no CreatedBy and POIID, it's a new POI, so all fields should be enabled
    setEditDisabled(isEditDisabled)
    
  }, []); //eslint-disable-line

  useEffect(() => {
    const usersPoiGroups = getUsersPoiGroups(poiId, data.poiGroups, usersGroupData, selectedPoiGroup)
    setPoiGroups(usersPoiGroups)
  }, [usersGroupData]); //eslint-disable-line

  //update state when data changes
  useEffect(() => {
    setDistance(data.poiMetersToPipeline);
    setUsSite(data.upstreamAndDownstreamAgms.upstreamAgm.Site_Name);
    setDsSite(data.upstreamAndDownstreamAgms.downstreamAgm.Site_Name);
    setDistToUs(data.usDsDistances.distToUS);
    setDistToDs(data.usDsDistances.distToDS);
    
    // Update modal with new intersection values if it's open
    if (showCustomIntersectModal) {
      setCustomIntersectError('');
    }
  }, [data, showCustomIntersectModal])

  /** HANDLER FUNCTIONS **/

  const handleDropdownSelect = (option) => {
    setSnapToSelection(option);
  }

  const handleSnapToBtnClick = () => {
    poiDialogHandlers.snapTo(snapToSelection);
  }

  const handleOpenCustomIntersectModal = () => {
    setCustomIntersectError('');
    setShowCustomIntersectModal(true);
  }

  const handleCustomIntersectSubmit = async(lat, lng) => {
    const result = poiDialogHandlers.handleCustomIntersect(lat, lng);
    
    if (result.success) {
      // Update dialog state with new calculated values
      setDistance(data.poiMetersToPipeline);
      setDistToUs(data.usDsDistances.distToUS);
      setDistToDs(data.usDsDistances.distToDS);
      // Close modal on success
      setShowCustomIntersectModal(false);
      setCustomIntersectError('');

      return {
        success: true
      }
    } else {
      // Display validation error in modal
      setCustomIntersectError(result.errorMessage);

      return {
        success: false
      }
    }
  }

  const handleCustomIntersectCancel = () => {
    setShowCustomIntersectModal(false);
    setCustomIntersectError('');
  }

  //get dropdown options depending on what's in upstreamAndDownstreamAgms
  //only show available sites and subchains
  const getSnapToOptions = () => {
    const dropdownOptions = [];
  
    const siteData = data.upstreamAndDownstreamAgms;
  
    const optionsMap = {
      upstreamAgm: "US Site",
      downstreamAgm: "DS Site",
      upstreamSc: "US Subchain",
      downstreamSc: "DS Subchain",
    };
  
    for (const key in optionsMap) {
      if (siteData[key]) {
        dropdownOptions.push(optionsMap[key]);
      }
    }
  
    return dropdownOptions;
  };
  
  const handleNameChange = (e) => {
    setName(e.target.value);
    poiDialogHandlers.handlePoiNameChange(e.target.value);
  }

  const handleDescriptionChange = (e) => {
    setDescription(e.target.value);
    poiDialogHandlers.handlePoiDescriptionChange(e.target.value);
  }

  const handleSave = async(e) => {
    e.preventDefault();

    //check if this poi is in any groups; if not, ask user if they'd like to add groups
    if(poiGroups.length === 0){
      const confirmAddToGroups = await requestConfirm('This POI has not been added to any of your groups. You must add it to a group to see it on Site Map and  Site Map w/PIG. Would you like to add it to your groups?', 'Yes, Add To My Groups', 'No, Continue Saving', 'Cancel');
      
      if(confirmAddToGroups === 'Cancel'){
        await poiDialogHandlers.handleDialogCancel()
        return
      } 

      if(confirmAddToGroups === 'Yes'){
        const response = await poiDialogHandlers.handlePoiSave(selectedPoiJob, null, false) //include 'false' so we don't close the window on save

        if(!response.success) return //usePoiDialogHandler already handles error modal, this is just for passing the POIID to group component for a new POI
    
        //update poiId and switch to group dialog component
        setPoiId(response.data.POIID)

        poiDialogHandlers.handleChangeActiveComponent("PoiGroupDialog")
        return
      } 
    }

    //if we've reached this point, there are poiGroups - just have to check if this is a new poi
    //if it is, there should be one gorup in poiGroups, which the user selected from the dropdown
    //if this is a new poi, send this group data so the new poi can be added to it
    if(!poiId){

      const addToGroup = !poiId ? poiGroups[0] : null

      poiDialogHandlers.handlePoiSave(selectedPoiJob, addToGroup)
    } else if(
      (selectedPoiGroup && !selectedPoiGroup.label.includes("All POIs")) && 
      !editDisabled && 
      poiGroups.some(group => group.POIGroupID === selectedPoiGroup.value) //making sure there is still a linkedPOI to update and it hasn't been removed from group
    ){
      //poi group is selected, so update the linkedPOI name and description 
      const poiGroup = poiGroups.find(poiGroup => poiGroup.POIGroupID === selectedPoiGroup.value)

      const linkedPOI = poiGroup.LinkedPOIs?.find(linkedPOI => linkedPOI.POIID === poiId)
      
      poiDialogHandlers.handleUpdateLinkedPOI(
        poiGroup.POIGroupID, 
        {
          ...linkedPOI,
          POI_Name: name,
          Description: description
        }, 
        poiId)

        //if user is admin, they can change poi details when the poi is in groups, so save poi as well
        if(userAdmin){
          poiDialogHandlers.handlePoiSave(selectedPoiJob)
        }

    } else {
      //just save original poi details
      poiDialogHandlers.handlePoiSave(selectedPoiJob)
    }
 
  };

  const handleDelete = async(e) => {
    e.preventDefault();
    await poiDialogHandlers.handlePoiDelete({POIID: poiId, CreatedBy: data.CreatedBy.UserID});
  };

  const handleClose = async(e) => {
    e.preventDefault();
    await poiDialogHandlers.handleDialogCancel();
  };

  const switchToGroupDialog = async(e) => {
    e.preventDefault();

    //save current poi if it doesn't already exist
    if(!poiId){
      const addToGroup = !poiId ? poiGroups[0] : null

      const response = await poiDialogHandlers.handlePoiSave(selectedPoiJob, addToGroup, false) //add false to prevent closing info window on save

      if(!response.success) return //usePoiDialogHandler already handles error modal, this is just for passing the POIID to group component for a new POI
    
      //update poiId and switch to group dialog component
      setPoiId(response.data.POIID)
    }
    
    //always switch components (unless there was an error saving - handled above with 'return')
    poiDialogHandlers.handleChangeActiveComponent("PoiGroupDialog")
  }
  

  /** MAIN COMPONENT **/

  return (
    <>

      <div
        className='border border-black rounded p-4'
      >
        <form
          onSubmit={handleSave}
        >
          {/* POI Name - what's displayed here depends on whether user on poi-edit or site-map page, and whether pois were fetched by all groups or by single group */}
          {/* this is determined in 'editDisabled' variable, calculated above */}

          <PoiDialogInput
            label="POI Name"
            value={name}
            onChange={handleNameChange}
            disabled={editDisabled}
          />

          {/* POI Description */}

          <div
            className='mb-1 flex justify-between items-center'
          >
            <label
              className='font-bold'
              htmlFor='poi-description'
            >Description: </label>
            
            <div className={`flex flex-col ${showDescription ? 'mb-2' : ''}`}>

              <p 
                className="text-xs font-semibold text-gray-700 my-2 self-end underline"
                onClick={() => setShowDescription(!showDescription)}
              >
                {
                  showDescription ? (
                    <>

                      Hide description
                      <i className="fa-solid fa-chevron-up ml-2"></i>
                      

                    </>
                  ) 
                  : (
                    <>
                      Show Description
                      <i className="fa-solid fa-chevron-down ml-2"></i>
                    </>
                  )
                }
              </p>

              {
                showDescription ? (
                  <textarea
                    id='poi-description'
                    className='border border-black rounded ml-5 text-center p-2'
                    type="text"
                    value={description}
                    onChange={handleDescriptionChange}
                    placeholder={editDisabled ? null : "Enter description..."}
                    disabled={editDisabled}
                  />
                ): null
              }

            </div>
          </div>

          {/* original name - used when we have fetched poi from groups */}
          {/* in this case, POI Name above is the alternate poi name */}
          {originalName ? (

            <div className='mb-1 flex justify-between items-center'>
              
              {/* original name label */}
              <label
                className='font-bold'
                htmlFor='poi-name'
              >
                Original Name: 
              </label>

              <div className={'flex flex-col'}>
                
                {/* toggle button to show or hide original name */}
                <p 
                  className="text-xs font-semibold text-gray-700 my-2 self-end underline"
                  onClick={() => setshowOriginalPOIName(!showOriginalPOIName)}
                >
                  {
                    showOriginalPOIName ? (
                      <>

                        Hide Original POI Name
                        <i className="fa-solid fa-chevron-up ml-2"></i>
                        
                      </>
                    ) 
                    : (
                      <>
                        Show Original POI Name
                        <i className="fa-solid fa-chevron-down ml-2"></i>
                      </>
                    )
                  }
                </p>

                {/* show original name */}
                {
                  showOriginalPOIName ? (
                    <input
                      id='original-poi-name'
                      className='border border-black rounded ml-5 text-center'
                      type="text"
                      value={originalName}
                      disabled
                    />
                  ) : null
                }

              </div>
            </div>

          ) 
          // if no original name, show nothing
          : null}


         
          {/* meters to pipeline */}
          <PoiDialogInput
              label="Meters to Pipeline"
              value={distance.toFixed(3)}
              onChange={(e) => setDistance(e.target.value)}
              disabled={true}
          />

          {/* Intersection with Pipeline Details */}
          <div className='border border-black rounded p-2 mb-1'>
            <h1 className='mb-2'>Intersection Details</h1>
            
            <PoiDialogInput
              label="Lat"
              value={data.lineIntersect.lat}
              disabled={editDisabled}
              intersection
            />

            <PoiDialogInput
              label="Lng"
              value={data.lineIntersect.lng}
              disabled={editDisabled}
              intersection
            />

            {/* snap to upstream or downstream agm */}
            {(currentPage.includes('/poi-edit') && userAdmin) || (!editDisabled && !latLngEditDisabled) ? (
              <div>
                <div className='my-1 flex justify-around items-center border border-gray-400 rounded p-2'>
                  <PoiDialogButton
                    text="Snap To"
                    variant="cancel"
                    onClick={handleSnapToBtnClick}
                    className="ml-4"
                  />

                  <SimpleDropdown options={getSnapToOptions()} onSelect={handleDropdownSelect} selectedOption={snapToSelection} />
                </div>
                
                <div className='mt-1 flex justify-center'>
                  <PoiDialogButton
                    text="Edit Lat/Lng"
                    variant="primary"
                    onClick={handleOpenCustomIntersectModal}
                  />
                </div>
              </div>
            )
            : null
            }
          
          </div>

          {/* Upstream & Downstream Site and Distance */}

          <PoiDialogInput
              label="Upstream Site"
              value={usSite}
              onChange={(e) => setUsSite(e.target.value)}
              disabled={true}
          />

          <PoiDialogInput
              label="Dist to Upstream (m)"
              value={distToUS.toFixed(3)}
              onChange={(e) => setDistToUs(e.target.value)}
              disabled={true}
          />

          <PoiDialogInput
              label="Downstream Site"
              value={dsSite}
              onChange={(e) => setDsSite(e.target.value)}
              disabled={true}
          />
          
          <PoiDialogInput
              label="Dist to Downstream (m)"
              value={distToDS.toFixed(3)}
              onChange={(e) => setDistToDs(e.target.value)}
              disabled={true}
          />

          {/* list of poi groups  */}
          
          {/* 'poiGroups' === the current user's groups that the POI is in */}

          <div className='border border-black rounded p-2 mb-1 flex flex-col'>
            
              {poiGroups && poiGroups.length > 0 ? (
                <>
                  <h1 className='mb-2'>
                    This POI is in your groups: <PoiGroupTooltip />  
                    
                  </h1>

                  <div
                    className='mb-1 flex ml-4'
                  >
                    <ul>
                      {poiGroups.map((poiGroup) => 
                        <li className='list-disc' key={`group-${poiGroup.POIGroupID}`}>{poiGroup.POIGroup_Name}</li>
                      )}
                    </ul> 
                  </div>
                </>
              ) : (
                <h1 className='mb-2'>
                  This POI is not in any of your groups
                  <PoiGroupTooltip />
                </h1>
              )} 
          
          {/* if not on poi-edit page, don't show  button - site-map pages are for viewing only, not editing */}
            {currentPage.includes("poi-edit") ? (

              <PoiDialogButton
                text={poiGroups && poiGroups.length > 0 ? "Add To / Remove From My Groups" : "Add POI To My Groups"}
                variant="cancel"
                onClick={(e) => switchToGroupDialog(e)}
                className="my-2 self-center"
              />

            ) 
            : null
            }
              
          </div>

          {/* original POI creator */}

          {createdBy ? (
            <PoiDialogInput
              label="POI Created By"
              value={`${createdBy.First_Name} ${createdBy.Last_Name}`}
              disabled={true}
            />
          ) : null}

          {/* estimated pass time (if run is active) */}

          {estimatedPassTime ? (
            <PoiDialogInput
              label="Estimated Pass Time"
              value={estimatedPassTime}
              disabled={true}
              className="w-44"
            />
          ) : null}

          {/* Save, Cancel, and Delete Buttons */}
          {/* show only the Cancel button if page is non-editable */}
          
          <div
            className={`mt-5 mb-1 flex ${editDisabled ? 'justify-end': 'justify-between'}`}
          >
            {/* Delete button */}
            {poiId && !editDisabled && canDeletePOI ? (
              
              <PoiDialogButton
                text="Delete"
                variant="danger"
                type="submit"
                onClick={handleDelete}
              />

            )
            : null
            }
            
            {/* div for Save and Cancel */}
            <div>

              {!editDisabled ? (
                <PoiDialogButton
                  text="Save"
                  variant="primary"
                  type="submit"
                />
              )  
              : null
              }
              
              <PoiDialogButton
                text="Close"
                variant="cancel"
                onClick={(e) => handleClose(e)}
                className="ml-4"
              />

            </div>

          </div>

        </form>
      </div>

      {/* if current poi isn't in any of the user's groups, ask to confirm before saving and exiting window */}
      <ConfirmDialog />

      {/* Modal for adding custom intersection point */}
      {showCustomIntersectModal && (
        <CustomIntersectModal
          currentLat={data.lineIntersect.lat}
          currentLng={data.lineIntersect.lng}
          onSubmit={handleCustomIntersectSubmit}
          onCancel={handleCustomIntersectCancel}
          validationError={customIntersectError}
          isEdit={true}
        />
      )}

    </>
  )
};

export default PoiDialog;
